import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { DType } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { ServiceJob } from '../../modules/admin/services/services.types';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { ServicesService } from '../../modules/admin/services/services.service';

@Component({
    selector: 'app-edit-service-job-dialog',
    templateUrl: './edit-service-job-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .edit-service-job-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditServiceJobDialogComponent implements OnInit, OnDestroy {

    dtypeForm: DTypeForm;
    hasChanges = false;

    private unsubscribeAll = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            job: ServiceJob;
            dtype: DType;
            update: boolean;
        },
        public dialogRef: MatDialogRef<EditServiceJobDialogComponent>,
        private dialogsService: DialogsService,
        private _fuseConfirmationService: FuseConfirmationService,
        private servicesService: ServicesService,
        private transloco: TranslocoService
    ) { }

    ngOnInit(): void {
        this.dtypeForm = this.servicesService.retrieveServiceJobDtypeFormFromDType(
            this.data.dtype,
            this.data.job,
            {
                readonly: false,
                isUpdate: this.data.update
            }
        );

        this.dtypeForm.control.valueChanges
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(() => this.hasChanges = true);
    }

    ngOnDestroy() {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    async closeDialog() {
        if (this.hasChanges) {
            const closeMasterDataConfirm = await this.closeConfirmDialog(this.transloco.translate('warning'), this.transloco.translate('closeMasterDataConfirm'));
            if (closeMasterDataConfirm) {
                this.dialogRef.close();
            }
        } else {
            this.dialogRef.close();
        }
    }

    save() {
        this.dtypeForm.control.markAllAsTouched();
        if (this.dtypeForm.control.invalid) {
            this.dialogsService.openErrorDialog({
                message: this.transloco.translate('errorsInForm')
            });
            return;
        }
        const job: ServiceJob = Object.assign(this.data.job ? this.data.job : {}, this.dtypeForm.control.value);
        this.dialogRef.close(job);
    }

    private closeConfirmDialog(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('unsavedChanges'),
            message: message ? message : this.transloco.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }
}
